import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import Select from "shared/components/select/Select";

import Collapse from "@kunukn/react-collapse";
import { Option } from "../../../shared/model/Option";
import { Utilities } from "../../../shared/utilities";
import { AppContext } from "../../App";
import { IAppReducerState, IPublicHoliday } from "../../shared/model";
import { hourArray, timeZonesAsOptions } from "../../shared/ruleEditorMappings";
import { INewRuleFormData } from "../RuleEditor";
import { HolidayPicker } from "./HolidayPicker";
import { CustomBooleanSwitch } from "shared/components/CustomSwitch";
import LabelWithTooltip from "shared/components/LabelWithTooltip";
import { Checkbox } from "shared/components/Checkbox";

const TimeZoneDateSettings: React.FC<IRuleEditorTimeZoneDateSettingsProps> = (props) => {

    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);

    const timeZones = timeZonesAsOptions(state?.timeZones);
    const weekDays = [
        {
            name: FRENDS.Localizations.Monday,
            value: "MON"
        },
        {
            name: FRENDS.Localizations.Tuesday,
            value: "TUE"
        },
        {
            name: FRENDS.Localizations.Wednesday,
            value: "WED"
        },
        {
            name: FRENDS.Localizations.Thursday,
            value: "THU"
        },
        {
            name: FRENDS.Localizations.Friday,
            value: "FRI"
        },
        {
            name: FRENDS.Localizations.Saturday,
            value: "SAT"
        },
        {
            name: FRENDS.Localizations.Sunday,
            value: "SUN"
        }
    ];

    const {
        formData,
        formDataErrors,
        handlers
    } = props;

    const handleActiveDaysToggle = () => {
        if (formData.isActiveEveryDay) {
            handlers.handleIsActiveOnSelectedDays();
        } else if (!formData.isActiveEveryDay) {
            handlers.handleIsActiveEveryDay();
        }
    }

    const handleActiveTimesToggle = () => {
        if (formData.isActiveEntireDay) {
            handlers.handleIsActiveBetweenSelectedTimes();
        } else if (!formData.isActiveEntireDay) {
            handlers.handleIsActiveEntireDay();
        }
    }
    const handleHoldaysToggle = () => {
        if (formData.disablingHolidaysAreEnabled) {
            handlers.handleHasInactiveHolidaysDisabled();
        } else if (!formData.disablingHolidaysAreEnabled) {
            handlers.handleHasInactiveHolidaysEnabled()
        }
    }


    return (
        <>
            <S.FormGroup>
                <LabelWithTooltip
                    label={FRENDS.Localizations.TimeZone}
                    description={FRENDS.Localizations.Info_RuleWillNotConsiderDaylightSavingsTime}
                />
                <Select
                    classNamePrefix="react-select"
                    options={timeZones as Option[]}
                    value={Utilities.valueFromId(timeZones, formData.timeZone)}
                    defaultValue={timeZones?.find((x) => x.value === "FLE Standard Time")}
                    onChange={handlers.handleTimeZone}
                />
            </S.FormGroup>

            <S.FormGroup>
                <CustomBooleanSwitch
                    trueLabel={FRENDS.Localizations.OnEveryDay}
                    falseLabel={FRENDS.Localizations.OnSelectedDays}
                    toggled={formData.isActiveEveryDay}
                    onChange={handleActiveDaysToggle}
                />
            </S.FormGroup>

            <Collapse isOpen={!formData.isActiveEveryDay}>
                <S.FormGroup style={{ marginLeft: 30 }} variant={formDataErrors.includes("activeWeekDays") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.InputGroup style={{ flexWrap: "wrap" }}>
                        {weekDays.map((x, idx) => {
                            return (
                                <S.FlexContainer key={idx} style={{ alignItems: "center", gap: 18 }}>
                                    <Checkbox
                                        id={idx + "checkbox"}
                                        value={x.value}
                                        defaultChecked={formData.activeWeekDays.includes(x.value)}
                                        onClick={handlers.handleActiveWeekdays}
                                    >
                                    </Checkbox>
                                    <label htmlFor={idx + "checkbox"}>{x.name}</label>
                                </S.FlexContainer>
                            );
                        })}
                    </S.InputGroup>
                    {formDataErrors.includes("activeWeekDays") && <span className="error">At least one active weekday is required.</span>}
                </S.FormGroup>
            </Collapse>


            <S.FormGroup>
                <CustomBooleanSwitch
                    trueLabel={FRENDS.Localizations.EntireDay}
                    falseLabel={FRENDS.Localizations.BetweenSelectedTimes}
                    toggled={formData.isActiveEntireDay}
                    onChange={handleActiveTimesToggle}
                />
            </S.FormGroup>

            <Collapse isOpen={!formData.isActiveEntireDay} overflowOnExpanded={true}>
                <S.FormGroup style={{ marginLeft: 30 }} variant={formDataErrors.includes("startHour") || formDataErrors.includes("endHour") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.InputGroup style={{ gap: 24, justifyContent: "flex-start" }}>
                        <div>
                            <S.Label htmlFor="ruleStartTime">{FRENDS.Localizations.StartTime}</S.Label>
                            <Select
                                classNamePrefix="react-select"
                                options={hourArray}
                                menuPlacement="top"
                                value={Utilities.valueFromId(hourArray, formData.startHour)}
                                onChange={handlers.handleStartHour}
                            />
                            {formDataErrors.includes("startHour") && <span className="error">The start hour is required.</span>}
                        </div>
                        <div>
                            <S.Label htmlFor="ruleEndTime">{FRENDS.Localizations.EndTime}</S.Label>
                            <Select
                                classNamePrefix="react-select"
                                options={hourArray}
                                menuPlacement="top"
                                value={Utilities.valueFromId(hourArray, formData.endHour)}
                                onChange={handlers.handleEndHour}
                            />
                            {formDataErrors.includes("endHour") && <span className="error">The end hour is required.</span>}
                        </div>
                    </S.InputGroup>
                </S.FormGroup>
            </Collapse>

            <S.FormGroup>
                <CustomBooleanSwitch
                    trueLabel={FRENDS.Localizations.DisablingHolidays}
                    falseLabel={FRENDS.Localizations.NoDisablingHolidays}
                    toggled={formData.disablingHolidaysAreEnabled}
                    onChange={handleHoldaysToggle}
                />
            </S.FormGroup>


            <Collapse isOpen={formData.disablingHolidaysAreEnabled} overflowOnExpanded={true}>
                <S.FormGroup>
                    <HolidayPicker
                        initialSelectedYear={new Date().getFullYear()}
                        initialSelectedCountry="FI"
                        handleSelectionChange={handlers.handleHolidaySelectionChange}
                        disablingHolidays={formData.disablingHolidays}
                    />
                </S.FormGroup>
            </Collapse>


        </>
    );
};

interface IRuleEditorTimeZoneDateSettingsProps {
    formData: INewRuleFormData;
    formDataErrors: string[];
    handlers: {
        handleTimeZone: (e: Option) => void;
        handleIsActiveEveryDay: () => void;
        handleIsActiveOnSelectedDays: () => void;
        handleActiveWeekdays: (e: any) => void;
        handleIsActiveEntireDay: () => void;
        handleIsActiveBetweenSelectedTimes: () => void;
        handleStartHour: (e: Option) => void;
        handleEndHour: (e: Option) => void;
        handleHasInactiveHolidaysEnabled: () => void;
        handleHasInactiveHolidaysDisabled: () => void;
        handleHolidaySelectionChange: (holidays?: IPublicHoliday[]) => void;
    };
}

export default TimeZoneDateSettings;
